import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { CurrencyInput } from "@/components/ui/currency-input";
import { FloppyDisk, CircleNotch } from "@phosphor-icons/react";
import { throwIfNotOk, apiSend } from "@/lib/api-error";
import { toast } from "@/lib/toast";
import { PAYOUT_REQUEST_MIN_KEY, parsePayoutRequestMin } from "@/lib/payout-request-limits-shared";

export function PayoutRequestMinSection() {
  const queryClient = useQueryClient();

  const { data: settings, isLoading } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => {
      const res = await fetch("/api/settings");
      await throwIfNotOk(res, "Greška pri učitavanju postavki");
      return (await res.json()) as Record<string, string>;
    },
  });

  const [min, setMin] = useState<number | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!settings || loaded) return;
    setMin(parsePayoutRequestMin(settings[PAYOUT_REQUEST_MIN_KEY]));
    setLoaded(true);
  }, [settings, loaded]);

  const saveMutation = useMutation({
    mutationFn: () =>
      apiSend(
        "/api/settings",
        "PATCH",
        { [PAYOUT_REQUEST_MIN_KEY]: min === null ? "" : String(min) },
        "Greška pri spremanju",
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settings"] });
      toast.success("Minimalni iznos spremljen");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const savedMin = parsePayoutRequestMin(settings?.[PAYOUT_REQUEST_MIN_KEY]);
  const isDirty = min !== savedMin;

  return (
    <div className="rounded-xl border border-border/60 bg-card p-4 shadow-sm">
      <h3 className="text-sm font-semibold text-foreground">Minimalni iznos za zahtjev za isplatu</h3>
      <p className="mt-1 text-xs text-muted-foreground">
        Terapeut ne može zatražiti isplatu dok pending iznos ne dostigne ovu vrijednost. Prazno polje znači bez praga.
      </p>

      {isLoading ? (
        <div className="py-6 text-center text-sm text-muted-foreground">Učitavanje...</div>
      ) : (
        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <Label htmlFor="payout-request-min">Minimalni iznos</Label>
            <CurrencyInput
              id="payout-request-min"
              value={min}
              onChange={setMin}
              placeholder="bez praga"
            />
          </div>
          <Button
            type="button"
            onClick={() => saveMutation.mutate()}
            disabled={!isDirty || saveMutation.isPending}
          >
            {saveMutation.isPending ? (
              <CircleNotch className="size-4 animate-spin" />
            ) : (
              <FloppyDisk className="size-4" />
            )}
            Sačuvaj
          </Button>
        </div>
      )}
    </div>
  );
}
